import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  createInitialDemoGuideState,
  DEMO_GUIDE_STATE_KEY,
  type AssetClassId,
  type DemoGuideState,
} from "./types";
import { visibleSteps } from "./visibleSteps";
import {
  trackDemoGuide,
  trackDemoGuideComplete,
  trackDemoGuideStep,
} from "./analytics";
import { applyDemoTemplate } from "./applyDemoTemplate";
import { demoTemplateFor } from "./templates";
import { setDemoWatchlist } from "@/pages/PortfolioBuilder/demoWatchlist";
import { demoWatchlistFor } from "./demoWatchlistFor";

type DemoGuideStore = {
  state: DemoGuideState | null;
  /** Create the initial state. Called once by the gate when the picker opens. */
  begin: () => void;
  /** A desk was picked: apply its template and start the guided run. */
  pickDesk: (assetClass: AssetClassId) => void;
  /** Picker declined — "just let me look around". */
  dismissPicker: () => void;
  /**
   * Advance the run. Completing the last step ends it.
   *
   * `skipped` separates "they did the thing" from "they pressed Skip". Both
   * move the run on, and only the first is a completed step — see the
   * step_skipped note in analytics.ts.
   */
  advanceStep: (opts?: { skipped?: boolean }) => void;
  /** Close the rail without finishing; progress is kept. */
  exitRun: () => void;
  /** Re-open the rail from the checklist. */
  resumeRun: () => void;
  /** The builder "saved" the demo watchlist — nothing was written. */
  markWatchlistCreated: () => void;
  markPoppedOut: () => void;
  dismissChecklist: () => void;
  /**
   * Start again from the desk picker, keeping nothing.
   *
   * Offered in the checklist because there was no way back: dismissing the
   * picker persisted to localStorage, so a visitor who clicked "look around"
   * could not see the guided demo again without clearing site data.
   */
  restart: () => void;
  /** Dev/testing only — wipes state so the picker re-triggers. */
  reset: () => void;
};

/**
 * Single writer for demo.guide.state.
 *
 * Persistence is localStorage via zustand's `persist`, not the pref-storage
 * round-trip src/onboarding/api.ts uses — an unauthenticated visitor has no
 * token. That also means no load/loadFailed states: the value is available
 * synchronously at first render, so the gate never waits on it.
 */
export const useDemoGuideStore = create<DemoGuideStore>()(
  persist(
    (set, get) => {
      const update = (
        updater: (prev: DemoGuideState) => DemoGuideState
      ): void => {
        const prev = get().state;
        if (!prev) return;
        set({ state: updater(prev) });
      };

      return {
        state: null,

        begin: () => {
          if (get().state) return; // already started
          set({ state: createInitialDemoGuideState() });
          trackDemoGuide("picker_shown");
        },

        pickDesk: (assetClass) => {
          const template = demoTemplateFor(assetClass);
          if (!template) return;

          // Free, instant, deterministic — no API call stands between the
          // answer and a dashboard that reflects it.
          applyDemoTemplate(template);
          // Gives the filter and highlight panels something real to offer —
          // /portfolios is 403 for this audience, so otherwise they are empty.
          // Resolved centrally so this and the gate's post-reload re-install
          // can never install different lists; see demoWatchlistFor.
          setDemoWatchlist(demoWatchlistFor(assetClass));
          trackDemoGuide("desk_picked", template.id);

          update((prev) => ({
            ...prev,
            assetClass,
            templateId: template.id,
            pickerAnsweredAt: prev.pickerAnsweredAt ?? new Date().toISOString(),
            step: 0,
            completedAt: undefined,
            exitedAt: undefined,
            watchlistCreatedAt: undefined,
            // Picking a desk starts a run, including when it re-picks after
            // one finished. Carrying the old list over would suppress every
            // demo_step in the second run as already reported.
            completedStepIds: [],
          }));
        },

        dismissPicker: () => {
          trackDemoGuide("picker_dismissed");
          update((prev) => ({
            ...prev,
            pickerAnsweredAt: new Date().toISOString(),
            exitedAt: new Date().toISOString(),
          }));
        },

        advanceStep: (opts) => {
          const current = get().state;
          if (!current?.assetClass || current.completedAt) return;

          // Against the steps THIS desk is running, not the full list. Steps
          // are dropped per desk (no filtered wire, no recording), so counting
          // against DEMO_STEPS left the run unable to finish: the index walked
          // past the end of the visible set and the rail rendered nothing
          // while completedAt was never written.
          const steps = visibleSteps(current.assetClass);
          const stepId = steps[current.step]?.id;
          const next = current.step + 1;
          const finished = next >= steps.length;

          const alreadyReported =
            !!stepId && (current.completedStepIds ?? []).includes(stepId);
          const completed = !opts?.skipped && !!stepId && !alreadyReported;

          if (opts?.skipped) {
            trackDemoGuide("step_skipped", stepId);
          } else if (completed) {
            trackDemoGuideStep({
              stepId,
              stepIndex: current.step,
              stepTotal: steps.length,
              templateId: current.templateId,
            });
          }

          const completedStepIds = completed
            ? [...(current.completedStepIds ?? []), stepId]
            : current.completedStepIds ?? [];

          if (finished) {
            trackDemoGuideComplete({
              templateId: current.templateId,
              stepsCompleted: completedStepIds.length,
            });
          }

          update((prev) => ({
            ...prev,
            step: next,
            completedStepIds,
            ...(finished && { completedAt: new Date().toISOString() }),
          }));
        },

        exitRun: () => {
          const current = get().state;
          if (current?.assetClass && !current.completedAt) {
            trackDemoGuide(
              "run_exited",
              visibleSteps(current.assetClass)[current.step]?.id
            );
          }
          update((prev) => ({ ...prev, exitedAt: new Date().toISOString() }));
        },

        resumeRun: () =>
          update((prev) => ({ ...prev, exitedAt: undefined })),

        markWatchlistCreated: () => {
          const current = get().state;
          if (!current?.assetClass || current.watchlistCreatedAt) return;
          trackDemoGuide("watchlist_created");

          update((prev) => ({
            ...prev,
            watchlistCreatedAt: new Date().toISOString(),
          }));

          // Advance out of the builder step. Leaving it on the "open
          // Watchlists" step meant the instruction said "go back and apply it"
          // while the step that lights the highlight control and watches for
          // the application was still one ahead — so nothing glowed on the
          // dashboard and the run waited on a Next button instead of on them.
          if (
            visibleSteps(current.assetClass)[current.step]?.id === "watchlist"
          ) {
            get().advanceStep();
          }
        },

        markPoppedOut: () =>
          update((prev) =>
            prev.poppedOutAt
              ? prev
              : { ...prev, poppedOutAt: new Date().toISOString() }
          ),

        dismissChecklist: () => {
          trackDemoGuide("checklist_dismissed");
          update((prev) => ({
            ...prev,
            checklistDismissedAt: new Date().toISOString(),
          }));
        },

        restart: () => {
          trackDemoGuide("restarted");
          setDemoWatchlist(null);
          set({ state: createInitialDemoGuideState() });
        },

        reset: () => {
          setDemoWatchlist(null);
          set({ state: null });
        },
      };
    },
    {
      name: DEMO_GUIDE_STATE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (store) => ({ state: store.state }),
    }
  )
);
